import { useContext, useState, useEffect } from "react";
import { Typography } from "@remo-co/ui-core/src/components/Typography";
import { Box } from "@remo-co/ui-core/src/components/Box";
import { Button } from "@remo-co/ui-core/src/components/Button";
import { useI18n } from "i18n";
import { IEvent } from "modules/event/types";
import { Events, TRACKING_CONTEXT } from "modules/tracking";
import { useCompanyPlanSettings } from "modules/companyPlanSettings";
import { isAgencyWhiteLabellingEnabled } from "modules/companyPlanSettings/utils/whitelabel";
import { useEmailCustomization } from "modules/emailCustomization/hooks/useEmailCustomization";
import { useSaveEmailCustomization } from "modules/emailCustomization/hooks/useSaveEmailCustomization";
import {
  EventEmailCustomizationType,
  EventEmailCustomizationMediaType,
  IEmailCustomization,
} from "modules/emailCustomization/types";
import { Loader } from "@remo-co/ui-core/src/components/Loader";
import { removeEmptyStringFields } from "modules/eventForm/emails/utils";
import { styles } from "./styles";
import { EmptyState } from "../EmptyState";
import { EmailCustomizationTabGroup } from "../EmailCustomizationTabGroup";
import { EmailCustomizationForm } from "../EmailCustomizationForm";

interface Props {
  eventData: IEvent;
}

export const EmailsTab = ({ eventData }: Props) => {
  const { t } = useI18n(["common", "eventForm", "url"]);
  const { myCompanyPlan } = useCompanyPlanSettings();
  const { track } = useContext(TRACKING_CONTEXT);
  const [selectedTab, setSelectedTab] = useState(
    EventEmailCustomizationType.INVITE,
  );
  const {
    customizations: emailCustomizations,
    isLoading: isEmailCustomizationsLoading,
  } = useEmailCustomization(eventData?.id);

  const [localCustomizations, setLocalCustomizations] = useState<
    IEmailCustomization[]
  >([]);

  useEffect(() => {
    if (emailCustomizations) {
      setLocalCustomizations(emailCustomizations);
    }
  }, [emailCustomizations]);

  const { saveCustomization, isLoading: isSaving } = useSaveEmailCustomization(
    eventData?.id,
  );

  const handleCustomizationChange = (
    updatedCustomizations: IEmailCustomization[],
  ) => {
    setLocalCustomizations(updatedCustomizations);
  };

  const handleSaveCustomizations = async () => {
    // Clean empty string fields before sending to the API
    const customizationsToSave = localCustomizations.map((customization) => {
      // Deep copy the customization to avoid mutation
      const cleanedCustomization = JSON.parse(JSON.stringify(customization));

      // Handle media fields based on media type
      if (
        cleanedCustomization.mediaType ===
        EventEmailCustomizationMediaType.IMAGE
      ) {
        delete cleanedCustomization.mediaExtraImageURL;
      } else if (
        cleanedCustomization.mediaType ===
        EventEmailCustomizationMediaType.VIDEO
      ) {
        if (!cleanedCustomization.mediaURL) {
          delete cleanedCustomization.mediaURL;
        }
        if (!cleanedCustomization.mediaExtraImageURL) {
          delete cleanedCustomization.mediaExtraImageURL;
        }
      }

      // Remove any empty string fields
      return removeEmptyStringFields(cleanedCustomization);
    });

    await saveCustomization(customizationsToSave);
    track(Events.EMAIL_CUSTOMIZATION_SAVED, { eventId: eventData?.id });
  };

  const handleResetCustomization = () => {
    const originalCustomization = emailCustomizations?.find(
      (c) => c.type === selectedTab,
    );

    let updatedCustomizations = [...localCustomizations];

    if (originalCustomization) {
      updatedCustomizations = updatedCustomizations.map((c) =>
        c.type === selectedTab ? { ...originalCustomization } : c,
      );
    } else {
      updatedCustomizations = updatedCustomizations.filter(
        (c) => c.type !== selectedTab,
      );
    }

    setLocalCustomizations(updatedCustomizations);
  };

  const showUpsell = !isAgencyWhiteLabellingEnabled(myCompanyPlan);

  return (
    <Box mb={5}>
      <div>
        <Typography variant="h2">
          {t("eventForm:customize.your.emails")}
        </Typography>
        <Box gap={1} display="flex" flexDirection="column">
          <Typography variant="body1" sx={styles.description}>
            {t("eventForm:customize.your.emails.description")}
          </Typography>
          <Typography variant="body1" sx={styles.description}>
            {t("eventForm:customize.your.emails.description.2")}
          </Typography>
        </Box>
        <Button<"a">
          variant="text"
          color="dark"
          size="md"
          sx={styles.helpLinkButton}
          href={t("url:email.customization.help.link")}
          target="_blank"
          rel="noreferrer"
        >
          {t("eventForm:customize.your.emails.learn.more")}
        </Button>
      </div>
      {showUpsell && <EmptyState />}
      {!showUpsell && (
        <>
          <EmailCustomizationTabGroup
            selectedTab={selectedTab}
            onChange={(tab) => setSelectedTab(tab)}
          />
          {isEmailCustomizationsLoading ? (
            <Box sx={styles.loadingContainer}>
              <Loader />
              <Typography variant="body1">{t("common:loading")}</Typography>
            </Box>
          ) : (
            <EmailCustomizationForm
              activeTab={selectedTab}
              customizations={localCustomizations}
              onCustomizationChange={handleCustomizationChange}
              onSave={handleSaveCustomizations}
              onReset={handleResetCustomization}
              isSaving={isSaving}
              eventId={eventData?.id}
            />
          )}
        </>
      )}
    </Box>
  );
};
